import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { ParserHlp } from 'src/lib/fliko/helpers/parser.helper';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { UserHlp } from 'src/lib/fliko/helpers/user.helper';
import { ProtocolType } from 'src/lib/fliko/models/agreements/protocol.data';
import { TenancyAgreementData } from 'src/lib/fliko/models/agreements/tenancyAgr.data';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { ResidentData, ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { ApartmentData } from 'src/lib/fliko/models/units/apartment.data';
import { ParkingData } from 'src/lib/fliko/models/units/parking.data';
import { StorageData } from 'src/lib/fliko/models/units/storage.data';
import { UnitCode } from 'src/lib/fliko/models/units/unitCode';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { ExcelFileFactory } from 'src/app/shared/factories/excel-file.factory';
import { GrammarHelper } from 'src/app/shared/helpers/grammar.helper';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { SelectInputData } from 'src/app/shared/interfaces/select-input-data.interface';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { NotesDataService } from 'src/app/shared/services/notes-data.service';
import { NoteExportHlp } from 'src/app/shared/models/note-table.model';
import { NoteVM } from 'src/app/shared/models/notes.view-model';
import { unitComparator } from '../../../helpers/unit-sort.helper';
import { CommonReport, PRSReport, PRSReportColumns } from '../../../services/models/table/users-report-table-model';
import { UserPRSList } from '../../../services/models/users-table.data';
import { UsersPRSDataService } from '../../../services/users-prs-data.service';

type ReportType = ('active' | 'archived' | 'future')[];

@Component({
  selector: 'app-users-prs-agreement-report',
  templateUrl: './users-prs-agreement-report.component.html',
  styleUrls: ['./users-prs-agreement-report.component.scss']
})
export class UsersPrsAgreementReportComponent implements OnInit, OnDestroy {
  private readonly locHelper = new LocalizationHelper(this.ls);

  public dataLoaded: boolean = false;
  // prettier-ignore
  public options: SelectInputData<string[]>[] = this.locHelper.localizeTitle([
    { title: 'agr.all',     value: ['active', 'archived', 'future'] },
    { title: 'agr.active',  value: ['active']                       },
    { title: 'agr.actFut',  value: ['active', 'future']             },
    { title: 'agr.future',  value: ['future']                       },
    { title: 'agr.archivals', value: ['archived']                   }
  ]);

  public reportTypeControl: FormControl = new FormControl(this.options[0].value);

  private agreementExportData: PRSReport.AgreementData[] = [];
  private residentExportData: PRSReport.ResidentData[] = [];
  private rentalUnitsExportData: PRSReport.RentalUnitData[] = [];
  private agreementExportNotes: PRSReport.AgreementsNote[] = [];
  private residentExportNotes: CommonReport.ResidentNote[] = [];

  private agreementTabData!: UserPRSList.AgreementElement[];
  private agreementNotes!: Map<string, NoteVM<TenancyAgreementData>[]>;
  private residentNotes!: Map<string, NoteVM<ResidentData>[]>;

  private subscription$ = new Subscription();

  constructor(
    private dialogRef: MatDialogRef<UsersPrsAgreementReportComponent>,
    private userPRSDataService: UsersPRSDataService,
    private errorHandlingService: ErrorHandlingService,
    private notesDataService: NotesDataService,
    private ls: LocalizationService
  ) {}

  ngOnInit(): void {
    this.subscription$.add(this.userPRSDataService.getAgreementsTabData$().subscribe((x) => (this.agreementTabData = x ?? [])));
    this.subscription$.add(
      this.notesDataService
        .getAgreementNotes$()
        .subscribe((x) => (this.agreementNotes = MapHlp.idMapArray(x, ['context.id'] as any[])))
    );
    this.subscription$.add(
      this.notesDataService
        .getResidentNotes$()
        .subscribe((x) => (this.residentNotes = MapHlp.idMapArray(x, ['context.id'] as any[])))
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public generateReport() {
    const t = (x: string) => this.ls.translate(x);
    const cap = (x: string) => StrHlp.capitalize(x);
    const reportType = this.reportTypeControl.value;
    this.prepareData(reportType);
    if (this.agreementTabData && this.agreementNotes && this.residentNotes) {
      const eg = new ExcelFileFactory();
      const hOpt = { height: 50 };

      eg.addSheet(cap(t('label.agreements')), PRSReportColumns.agreement(reportType), this.agreementExportData, this.ls, hOpt);
      eg.addSheet(t('navigation.units'), PRSReportColumns.rentalUnits, this.rentalUnitsExportData, this.ls, hOpt);
      eg.addSheet(cap(t('agr.residents')), PRSReportColumns.landlordTenant, this.residentExportData, this.ls, hOpt);
      const agrNotes = eg.addSheet(
        `${cap(t('label.agreements'))} - ${t('label.notes')}`,
        PRSReportColumns.agreementsNotes,
        this.agreementExportNotes,
        this.ls,
        hOpt
      );
      NoteExportHlp.format(eg, agrNotes, ['B'], ['E', 'F']);

      const resNotes = eg.addSheet(
        `${cap(t('agr.residents'))} - ${t('label.notes')}`,
        CommonReport.residentsNotesColumns,
        this.residentExportNotes,
        this.ls,
        hOpt
      );
      NoteExportHlp.format(eg, resNotes, ['B'], ['E', 'F']);

      eg.saveToFile(
        `${cap(t('label.report'))}_${this.getReportTypeHumanReadable(reportType)}_` +
          this.locHelper.localizeDate(Date.now(), 'dShort')
      );
      this.dialogRef.close();
    } else {
      this.errorHandlingService.showGlobalError(t('error.noData'));
    }
  }

  private getReportTypeHumanReadable(type: ReportType): string {
    return this.options.find((o) => ObjHlp.cmpStr(type, o.value, (x) => x.join(',')) == 0)?.title ?? '';
  }

  private prepareData(reportType: ReportType) {
    const tCap = (x: string) => StrHlp.capitalize(this.ls.translate(x));
    const fd = (x: Date | number | null | undefined) => this.locHelper.localizeDate(x, 'dShort');
    if (this.agreementTabData.length) {
      const uniqueResidents = new Set<ResidentData>();

      this.agreementTabData
        .filter((agr) => reportType.includes(agr.status))
        .sort((a, b) => b.agreement.agreementStartDate - a.agreement.agreementStartDate)
        .forEach((listData) => {
          const agreement = listData.agreement;
          const inf = tCap('label.infinite');
          const endDate = agreement.infiniteTimePeriod ? inf : fd(agreement.agreementEndDate);
          const formerEndDate = agreement.formerEndDate == -1 ? inf : fd(agreement.formerEndDate);
          const protocolDate = listData.protocols.find((p) => p.type === ProtocolType.ACCEPTANCE)?.timestamp;
          const status = UserHlp.agreementStatus(agreement);
          const agreementNumber = agreement.agreementNumber;
          const units: string[] = [];

          listData.apartments.forEach((vm) => {
            if (vm) {
              units.push(ToString.shortNumber(vm.unit));
              const apt = vm.unit as ApartmentData;
              this.rentalUnitsExportData.push({
                id: `#${this.rentalUnitsExportData.length + 1}`,
                agreementNumber,
                type: tCap('PRSapartment.nominative'),
                rentStartDate: fd(vm.tenancyDetails?.rentStartDate),
                rentEndDate: fd(vm.tenancyDetails?.rentEndDate),
                street: apt.street || '',
                blockNumber: apt.blockNumber,
                staircaseNumber: apt.staircaseNumber,
                floor: apt.floor,
                number: apt.number,
                area: apt.area ?? 0,
                unitCodes: UnitCode.allFromUnit(apt).join(', ')
              });
            }
          });

          listData.parkingSpots.forEach((vm) => {
            if (vm) {
              units.push(ToString.shortNumber(vm.unit));
              const par = vm.unit as ParkingData;
              this.rentalUnitsExportData.push({
                id: `#${this.rentalUnitsExportData.length + 1}`,
                agreementNumber,
                type: tCap('parking.nominative'),
                rentStartDate: fd(vm.tenancyDetails?.rentStartDate),
                rentEndDate: fd(vm.tenancyDetails?.rentEndDate),
                street: par.street || '',
                blockNumber: par.blockNumber || '',
                staircaseNumber: '',
                floor: par.floor || '',
                number: par.number,
                area: 0,
                unitCodes: UnitCode.allFromUnit(par).join(', ')
              });
            }
          });

          listData.storages.forEach((vm) => {
            if (vm) {
              units.push(ToString.shortNumber(vm.unit));
              const sto = vm.unit as StorageData;
              this.rentalUnitsExportData.push({
                id: `#${this.rentalUnitsExportData.length + 1}`,
                agreementNumber,
                type: tCap('storage.nominative'),
                rentStartDate: fd(vm.tenancyDetails?.rentStartDate),
                rentEndDate: fd(vm.tenancyDetails?.rentEndDate),
                street: sto.street || '',
                blockNumber: sto.blockNumber || '',
                staircaseNumber: '',
                floor: sto.floor || '',
                number: sto.number,
                area: sto.area ?? 0,
                unitCodes: UnitCode.allFromUnit(sto).join(', ')
              });
            }
          });

          this.agreementExportData.push({
            id: `#${this.agreementExportData.length + 1}`,
            agreementNumber: agreement.agreementNumber,
            bankAccount: ParserHlp.parseIbanDisplay(agreement.bankAccount),
            startDate: fd(agreement.agreementStartDate),
            endDate: endDate,
            formerEndDate,
            status: GrammarHelper.agrStatusHR(status, this.ls),
            protocolDate: fd(protocolDate),
            rent: agreement.rent,
            deposit: agreement.deposit,
            paymentDay: agreement.paymentDay,
            currency: agreement.currency,
            serviceCharge: agreement.serviceCharge,
            recipientNumber: agreement.recipientNumber || '',
            units: units.join(', ')
          });

          const agrNotes = this.agreementNotes.get(agreement.id);
          if (agrNotes) this.agreementExportNotes.push(...this.prepareNotes(agrNotes, agreement.agreementNumber));

          const residentToExportData = (r: ResidentData, type: ResidentType) => {
            uniqueResidents.add(r);
            this.residentExportData.push({
              id: `#${this.residentExportData.length + 1}`,
              agreementNumber,
              type: GrammarHelper.getResidentInCase(EstateType.PRS, type, 'M', this.ls),
              nameSurname: ToString.fullName(r),
              address: this.locHelper.addressToString(r.address),
              phone: StrHlp.join(r.phone),
              email: StrHlp.join(r.email),
              birthDate: this.locHelper.localizeDate(r.birthDate, 'dShort'),
              documentNo: r.documentNo ?? '',
              PESEL: r.PESEL ?? '',
              nationality: r.nationality ?? '',
              ownerCode: r.ownerCode ?? '',
              NIP: r.NIP ?? '',
              REGON: r.REGON ?? '',
              emergencyContact: ToString.fullNameContact(r.emergencyContact),
              lastUpdate: this.locHelper.localizeDate(r.lastUpdate, 'dtShort', this.ls.translate('label.never'))
            });
          };

          listData.landlords.forEach((landlord) => residentToExportData(landlord, ResidentType.LANDLORD));
          listData.tenants.forEach((tenant) => residentToExportData(tenant, ResidentType.TENANT));
        });

      this.rentalUnitsExportData = this.rentalUnitsExportData.sort(unitComparator);

      uniqueResidents.forEach((resident) => {
        const resNotes = this.residentNotes.get(resident.id);
        if (resNotes) this.residentExportNotes.push(...this.prepareNotes(resNotes, ToString.fullName(resident)));
      });
    }
  }

  private prepareNotes<T>(notes: NoteVM<T>[], header: string): CommonReport.ResidentNote[] {
    return notes.map((note, i) => {
      return { name: !i ? header : null, ...NoteExportHlp.createRow(note, i, this.ls) };
    });
  }
}
